import { useEffect, useState } from "react";
import { Col, message, Popconfirm, Row, Table } from "antd";
import Text from "antd/es/typography/Text";
import { useQuery } from "@tanstack/react-query";
import {
  downloadFileById,
  getGeneralInfoByInstitutionId,
  updateGeneralInfo,
} from "../../services/general-information.service";
import {
  generalInfoContent,
  generalInformationSchema,
  generalInformationValues,
} from "./general-information-utils";
import GeneralInformationInterface, {
  GeneralAttachedFile,
} from "../../interfaces/general-information.interface";
import { CBHandMessage } from "../shared/CBHandMessage/CBHandMessage";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import EditMenu from "./edit-menu";
import { CBQuill } from "../shared";
import CBFiles from "../shared/CBFiles/CBFiles";
import type { UploadFile } from "antd";
import { ColumnsType } from "antd/es/table";
import { DeleteOutlined, FolderOpenOutlined } from "@ant-design/icons";
import { useRole } from "../../hooks/user-roles";
import CustomCard from "../shared/CustomCard/CustomCard";
import { useDimensions } from "../../hooks/useDimensions";

interface FilesDataType extends GeneralAttachedFile {
  key: React.Key;
}

let columns: ColumnsType<FilesDataType> = [
  {
    title: "Document Information",
    dataIndex: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
    sortDirections: ["descend"],
    render: (_: any, record: { key: React.Key }) => (
      <>
        <div
          style={{ flex: 5, cursor: "pointer" }}
          onClick={() => downloadFileById(String(record.key), _)}
        >
          <FolderOpenOutlined style={{ fontSize: "20px" }} />
          <Text underline style={{ paddingLeft: "10px" }}>
            {_}
          </Text>
        </div>
      </>
    ),
  },
  {
    title: "Size",
    dataIndex: "size",
    sorter: (a, b) => a.size.localeCompare(b.size),
    sortDirections: ["descend"],
  },
];

interface generalInformationProps {
  partial?: boolean;
  courseDetail?: boolean;
  codeOfConduct?: (value: string) => void;
}
const GeneralInformation = ({
  partial,
  courseDetail,
  codeOfConduct,
}: generalInformationProps) => {
  const [generalInfo, setGeneralInfo] = useState<
    GeneralInformationInterface | undefined
  >(undefined);
  const institutionId = "1";
  const id = "1";
  const {
    isPending,
    error,
    data: generalInfoData,
  } = useQuery({
    queryKey: [`general-information-${id}`],
    queryFn: () => getGeneralInfoByInstitutionId(institutionId),
  });

  const { isAdmin } = useRole();
  const { width } = useDimensions();
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const values = generalInformationValues(generalInfo);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [files, setFiles] = useState<FilesDataType[]>([]);
  const [deletedFiles, setDeletedFiles] = useState<number[]>([]);

  const handleFiles = (files: GeneralAttachedFile[] = []) => {
    if (files) {
      setFiles(
        files.map((item) => {
          return {
            ...item,
            key: item.id,
            size: `${(Number(item.size) / 1e6).toFixed(2)} MB`,
          };
        }),
      );
    }
  };

  useEffect(() => {
    handleFiles(generalInfoData?.files);
    if (codeOfConduct) codeOfConduct(generalInfo?.studentCode ?? "");
  }, [generalInfoData]);

  const editColumns = [
    ...columns,
    {
      title: "Actions",
      dataIndex: "operation",
      render: (_: any, record: { key: React.Key }) =>
        files.length >= 1 ? (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => handleFileDelete(record.key)}
          >
            <DeleteOutlined />
          </Popconfirm>
        ) : null,
    },
  ];

  const handleFileDelete = (key: React.Key) => {
    const newData = files.filter((item) => item.key !== key);
    setDeletedFiles([...deletedFiles, Number(key)]);
    setFiles(newData);
  };

  const submitHandler = () => {
    let newValues = getValues();
    updateGeneralInfo(
      {
        info: {
          id: id,
          programDetails: newValues["programDetails"] ?? "",
          minimumRequirements: newValues["minimumRequirements"] ?? "",
          requirementsAuditing: newValues["requirementsAuditing"] ?? "",
          communications: newValues["communications"] ?? "",
          electronicSubmission: newValues["electronicSubmission"] ?? "",
          codeConduct: newValues["codeConduct"] ?? "",
          importantNotes: newValues["importantNotes"] ?? "",
          chequePayable: newValues["chequePayable"] ?? "",
          sendChequesForms: newValues["sendChequesForms"] ?? "",
          refunds: newValues["refunds"] ?? "",
          withdrawal: newValues["withdrawal"] ?? "",
          studentCode: newValues["studentCode"] ?? "",
          deletedFiles: deletedFiles,
        },
        files: fileList,
      },
      parseInt(id),
    )
      .then((data) => {
        const newGeneralInfo = data.data.generalInformation;
        setGeneralInfo(newGeneralInfo);
        setFileList([]);
        setDeletedFiles([]);
        handleFiles(newGeneralInfo?.files);
      })
      .catch(() => {
        message.error(
          "There was an unexpected error updating the General Information, please try again later",
        );
      });
  };

  const { control, getValues } = useForm({
    mode: "onBlur",
    resolver: yupResolver(generalInformationSchema),
    defaultValues: values,
    values: values,
  });

  if (isPending) {
    return <h4>Loading...</h4>;
  }
  if (error) {
    return <h4>Error </h4>;
  }
  if (!generalInfoData) {
    return <Text>General Information not found</Text>;
  }
  if (!generalInfo) {
    setGeneralInfo(generalInfoData);
  }
  const handleMenuChange = (s: boolean) => {
    setIsEditable(s);
    setFileList([]);
  };
  return (
    <>
      <div
        className={`flex flex-col justify-center max-w-6xl ${!courseDetail ? "p-4" : undefined}`}
        style={{ margin: "0 auto" }}
      >
        {!partial && isAdmin && (
          <div className="pt-4">
            <CBHandMessage color="yellow">
              The following information is applicable to all courses. Changes
              will show within.
            </CBHandMessage>
          </div>
        )}
        <div className="flex justify-center">
          <div
            className={`${!courseDetail ? "max-w-5xl mt-6 mr-5" : undefined}`}
          >
            <Row className={`${!courseDetail ? "mb-6" : undefined}`}>
              <Col span={24}>
                {!courseDetail && (
                  <CustomCard title="Certificate Information">
                    <div className="flex flex-col gap-4 py-6">
                      {isEditable ? (
                        <>
                          <div className="px-4">
                            {" "}
                            <CBQuill
                              control={control}
                              name="programDetails"
                              label="Program Details"
                            />{" "}
                          </div>
                          <div className="px-4">
                            {" "}
                            <CBQuill
                              control={control}
                              name="minimumRequirements"
                              label="Minimum Requirements"
                            />{" "}
                          </div>
                          <div className="px-4">
                            {" "}
                            <CBQuill
                              control={control}
                              name="requirementsAuditing"
                              label="Auditing Courses"
                            />{" "}
                          </div>
                        </>
                      ) : (
                        <>
                          {generalInfo?.programDetails &&
                            generalInfoContent(
                              "programDetails",
                              "Program Details",
                              generalInfo,
                            )}
                          {generalInfo?.minimumRequirements &&
                            generalInfoContent(
                              "minimumRequirements",
                              "Minimum Requirements",
                              generalInfo,
                            )}
                          {generalInfo?.requirementsAuditing &&
                            generalInfoContent(
                              "requirementsAuditing",
                              "Auditing Courses",
                              generalInfo,
                            )}
                        </>
                      )}
                    </div>
                  </CustomCard>
                )}
              </Col>
            </Row>
            <div
              className={`flex gap-8 ${width <= 500 ? "flex-wrap" : undefined} ${!courseDetail ? "mb-6" : undefined}`}
            >
              <div>
                <CustomCard title="Important Information">
                  <div className="flex flex-col gap-4 py-6">
                    {isEditable ? (
                      <>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="communications"
                            label="Communications"
                          />{" "}
                        </div>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="electronicSubmission"
                            label="Electronic Submission of Assignments"
                          />{" "}
                        </div>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="codeConduct"
                            label="Student code of conduct"
                          />{" "}
                        </div>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="importantNotes"
                            label="Important Notes"
                          />{" "}
                        </div>
                      </>
                    ) : (
                      <>
                        {generalInfo?.communications &&
                          generalInfoContent(
                            "communications",
                            "Communications",
                            generalInfo,
                          )}
                        {generalInfo?.electronicSubmission &&
                          generalInfoContent(
                            "electronicSubmission",
                            "Electronic Submission of Assignments",
                            generalInfo,
                          )}
                        {generalInfo?.codeConduct &&
                          generalInfoContent(
                            "codeConduct",
                            "Student Code of Conduct",
                            generalInfo,
                          )}
                        {generalInfo?.importantNotes &&
                          generalInfoContent(
                            "importantNotes",
                            "Important Notes",
                            generalInfo,
                          )}
                      </>
                    )}
                  </div>
                </CustomCard>
              </div>
              <div>
                <CustomCard title="Payment and Refunds">
                  <div className="flex flex-col gap-4 py-6">
                    {isEditable ? (
                      <>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="chequePayable"
                            label="Make cheque payable to"
                          />{" "}
                        </div>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="sendChequesForms"
                            label="Send cheques and other forms to"
                          />{" "}
                        </div>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="refunds"
                            label="Refunds"
                          />{" "}
                        </div>
                        <div className="px-4">
                          {" "}
                          <CBQuill
                            control={control}
                            name="withdrawal"
                            label="Withdrawal"
                          />{" "}
                        </div>
                      </>
                    ) : (
                      <>
                        {generalInfo?.chequePayable &&
                          generalInfoContent(
                            "chequePayable",
                            "Make Cheque Payable to",
                            generalInfo,
                          )}
                        {generalInfo?.sendChequesForms &&
                          generalInfoContent(
                            "sendChequesForms",
                            "Send Cheques and Other forms to",
                            generalInfo,
                          )}
                        {generalInfo?.refunds &&
                          generalInfoContent("refunds", "Refunds", generalInfo)}
                        {generalInfo?.withdrawal &&
                          generalInfoContent(
                            "withdrawal",
                            "Withdrawal",
                            generalInfo,
                          )}
                      </>
                    )}
                  </div>
                </CustomCard>
              </div>
            </div>
            <Row className={`${!courseDetail ? "mb-6" : undefined}`}>
              <Col span={24}>
                {!partial && (
                  <CustomCard title="Student Code of Conduct">
                    <div className="flex flex-col gap-4 py-6">
                      {isEditable ? (
                        <>
                          <div className="px-4">
                            {" "}
                            <CBQuill
                              control={control}
                              name="studentCode"
                              label="Student Code of Conduct"
                            />{" "}
                          </div>
                        </>
                      ) : (
                        <>
                          {generalInfo?.studentCode &&
                            generalInfoContent(
                              "studentCode",
                              "Student Code of Conduct",
                              generalInfo,
                            )}
                        </>
                      )}
                    </div>
                  </CustomCard>
                )}
              </Col>
            </Row>
            <Row className={`${!courseDetail ? "mb-6" : undefined}`}>
              <Col span={16} offset={4}>
                {!partial && (
                  <CustomCard title="Important Files">
                    <Row className="my-6">
                      <Col span={24}>
                        <div className="p-3">
                          {isEditable && (
                            <CBFiles
                              {...{ fileList, setFileList }}
                              name="files"
                            />
                          )}
                          <Table
                            columns={isEditable ? editColumns : columns}
                            dataSource={files}
                            bordered={true}
                            pagination={false}
                          />
                        </div>
                      </Col>
                    </Row>
                  </CustomCard>
                )}
                {!partial && (
                  <div>
                    {isAdmin && width <= 500 && (
                      <div className="h-12">
                        <div className="fixed bottom-16 left-36 z-10">
                          <EditMenu
                            handleChange={(s: boolean) => handleMenuChange(s)}
                            handleSubmit={() => submitHandler()}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!partial && (
            <div>
              {isAdmin && width >= 500 && (
                <EditMenu
                  handleChange={(s: boolean) => handleMenuChange(s)}
                  handleSubmit={() => submitHandler()}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GeneralInformation;
